import React, { useState, useEffect, useRef } from "react";
import AdminLayout from "../../../layouts/AdminLayout/AdminLayout";
import "../../../assets/css/admin/Dashboard.css";

const useCountUp = (end, duration = 1500, delay = 0) => {
  const [count, setCount] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const hasRun = useRef(false);
  useEffect(() => {
    if (hasRun.current || end === 0) return;
    hasRun.current = true;
    const easeOut = (t) => 1 - Math.pow(1 - t, 3);
    const run = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const p = Math.min((ts - startRef.current) / duration, 1);
      setCount(Math.floor(easeOut(p) * end));
      if (p < 1) rafRef.current = requestAnimationFrame(run);
      else setCount(end);
    };
    const t =
      delay > 0
        ? setTimeout(() => {
            rafRef.current = requestAnimationFrame(run);
          }, delay)
        : null;
    if (!t) rafRef.current = requestAnimationFrame(run);
    return () => {
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
      if (t) clearTimeout(t);
    };
  }, [end, duration, delay]);
  return count;
};

const useInView = () => {
  const ref = useRef(null);
  const [inView, setInView] = useState(false);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setInView(true);
          obs.disconnect();
        }
      },
      { threshold: 0.15 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return [ref, inView];
};

const StatCard = ({ card, index, inView }) => {
  const raw = parseInt(card.count.replace(/,/g, ""), 10);
  const n = useCountUp(inView ? raw : 0, 1500, index * 120);
  const fmt = raw >= 1000 ? n.toLocaleString() : n;
  return (
    <div
      className="adm-stat-card adm-fade-up"
      style={{ animationDelay: `${index * 0.1}s`, animationFillMode: "both" }}
    >
      <div className="adm-stat-card-top">
        <div className="adm-stat-icon">{card.icon}</div>
        <span
          className={`adm-delta ${card.deltaUp ? "delta-up" : "delta-down"}`}
        >
          {card.delta}
        </span>
      </div>
      <p className="adm-stat-number">{inView ? fmt : 0}</p>
      <h3 className="adm-stat-title">{card.title}</h3>
    </div>
  );
};

const AnimatedLineChart = ({ data, maxVal }) => {
  const [drawn, setDrawn] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setDrawn(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  const sPoints = data
    .map((d, i) => `${50 + i * 60},${140 - (d.students / maxVal) * 120}`)
    .join(" ");
  const mPoints = data
    .map((d, i) => `${50 + i * 60},${140 - (d.mentors / maxVal) * 120}`)
    .join(" ");
  return (
    <svg ref={ref} viewBox="0 0 400 180" className="adm-svg-chart">
      <defs>
        <linearGradient id="sgGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#781c2e" stopOpacity="0.18" />
          <stop offset="100%" stopColor="#781c2e" stopOpacity="0" />
        </linearGradient>
        <linearGradient id="mgGrad" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stopColor="#a52a3a" stopOpacity="0.14" />
          <stop offset="100%" stopColor="#a52a3a" stopOpacity="0" />
        </linearGradient>
        <clipPath id="lc-reveal">
          <rect
            x="0"
            y="0"
            width={drawn ? 400 : 0}
            height="200"
            style={{ transition: "width 1.4s cubic-bezier(0.4,0,0.2,1)" }}
          />
        </clipPath>
      </defs>
      {[0.25, 0.5, 0.75, 1].map((f, i) => (
        <line
          key={i}
          x1="40"
          y1={10 + f * 130}
          x2="390"
          y2={10 + f * 130}
          stroke="#e5e7eb"
          strokeWidth="1"
          strokeDasharray="4,4"
        />
      ))}
      <g
        style={{ opacity: drawn ? 1 : 0, transition: "opacity 0.6s ease 0.9s" }}
      >
        <polygon fill="url(#sgGrad)" points={sPoints + " 350,140 50,140"} />
        <polygon fill="url(#mgGrad)" points={mPoints + " 350,140 50,140"} />
      </g>
      <g clipPath="url(#lc-reveal)">
        <polyline
          fill="none"
          stroke="#781c2e"
          strokeWidth="2.5"
          strokeLinejoin="round"
          points={sPoints}
        />
        <polyline
          fill="none"
          stroke="#a52a3a"
          strokeWidth="2"
          strokeLinejoin="round"
          strokeDasharray="6,4"
          points={mPoints}
        />
      </g>
      {data.map((d, i) => (
        <g
          key={i}
          style={{
            opacity: drawn ? 1 : 0,
            transition: `opacity 0.3s ease ${0.9 + i * 0.08}s`,
          }}
        >
          <circle
            cx={50 + i * 60}
            cy={140 - (d.students / maxVal) * 120}
            r="4"
            fill="#781c2e"
            stroke="#fff"
            strokeWidth="2"
          />
          <circle
            cx={50 + i * 60}
            cy={140 - (d.mentors / maxVal) * 120}
            r="3.5"
            fill="#a52a3a"
            stroke="#fff"
            strokeWidth="2"
          />
          <text
            x={50 + i * 60}
            y="165"
            textAnchor="middle"
            className="adm-axis-label"
          >
            {d.month}
          </text>
        </g>
      ))}
    </svg>
  );
};

const AnimatedDonut = ({ slices, total }) => {
  const [vis, setVis] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setVis(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <svg
      ref={ref}
      viewBox="0 0 220 200"
      className="adm-svg-donut"
      style={{
        transform: vis ? "scale(1)" : "scale(0.7)",
        opacity: vis ? 1 : 0,
        transition:
          "transform 0.6s cubic-bezier(0.34,1.56,0.64,1), opacity 0.5s ease",
      }}
    >
      {slices.map((d, i) => (
        <path
          key={i}
          d={d.path}
          fill={d.color}
          stroke="#fff"
          strokeWidth="2"
          style={{
            transformOrigin: "110px 100px",
            transform: vis ? "scale(1)" : "scale(0.5)",
            opacity: vis ? 1 : 0,
            transition: `transform 0.5s cubic-bezier(0.34,1.56,0.64,1) ${i * 0.12}s, opacity 0.4s ease ${i * 0.12}s`,
          }}
        >
          <title>
            {d.level}: {d.count}
          </title>
        </path>
      ))}
      <circle cx="110" cy="100" r="40" fill="#fff" />
      <text x="110" y="95" textAnchor="middle" className="adm-donut-label">
        Total
      </text>
      <text x="110" y="116" textAnchor="middle" className="adm-donut-count">
        {total}
      </text>
    </svg>
  );
};

const AnimatedBar = ({
  x,
  targetH,
  maxY,
  color,
  label,
  value,
  delay,
  animate,
}) => {
  const [h, setH] = useState(0);
  const rafRef = useRef(null);
  const startRef = useRef(null);
  const fired = useRef(false);
  useEffect(() => {
    if (!animate || fired.current) return;
    fired.current = true;
    const DUR = 1200;
    const ease = (t) => 1 - Math.pow(1 - t, 3);
    const run = (ts) => {
      if (!startRef.current) startRef.current = ts;
      const p = Math.min((ts - startRef.current) / DUR, 1);
      setH(ease(p) * targetH);
      if (p < 1) rafRef.current = requestAnimationFrame(run);
      else setH(targetH);
    };
    const t = setTimeout(() => {
      rafRef.current = requestAnimationFrame(run);
    }, delay);
    return () => {
      clearTimeout(t);
      if (rafRef.current) cancelAnimationFrame(rafRef.current);
    };
  }, [animate, targetH, delay]);
  return (
    <g>
      <rect x={x} y={maxY - h} width="26" height={h} fill={color} rx="4" />
      <text
        x={x + 13}
        y={maxY - h - 4}
        textAnchor="middle"
        className="adm-bar-val"
        style={{ opacity: h > 10 ? 1 : 0, transition: "opacity 0.3s" }}
      >
        {value}
      </text>
      <text x={x + 13} y="168" textAnchor="middle" className="adm-axis-label">
        {label}
      </text>
    </g>
  );
};

const AnimatedBarChart = ({ data, maxVal }) => {
  const [animate, setAnimate] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const obs = new IntersectionObserver(
      ([e]) => {
        if (e.isIntersecting) {
          setAnimate(true);
          obs.disconnect();
        }
      },
      { threshold: 0.2 },
    );
    obs.observe(el);
    return () => obs.disconnect();
  }, []);
  return (
    <svg ref={ref} viewBox="0 0 300 180" className="adm-svg-chart">
      {[0.33, 0.66, 1].map((f, i) => (
        <line
          key={i}
          x1="20"
          y1={10 + f * 130}
          x2="290"
          y2={10 + f * 130}
          stroke="#e5e7eb"
          strokeWidth="1"
          strokeDasharray="4,4"
        />
      ))}
      {data.map((d, i) => (
        <AnimatedBar
          key={i}
          x={28 + i * 38}
          targetH={(d.sessions / maxVal) * 120}
          maxY={150}
          color={d.sessions === maxVal ? "#781c2e" : "#c8b9bc"}
          label={d.day}
          value={d.sessions}
          delay={i * 60}
          animate={animate}
        />
      ))}
    </svg>
  );
};

const ReportsSummaryBar = ({ reportsData }) => {
  const [ref, inView] = useInView();
  const total = reportsData.reduce((s, d) => s + d.count, 0);
  const openN = useCountUp(inView ? reportsData[0].count : 0, 1500, 0);
  const resolvedN = useCountUp(inView ? reportsData[1].count : 0, 1500, 80);
  const pendingN = useCountUp(inView ? reportsData[2].count : 0, 1500, 160);
  const rateN = useCountUp(inView ? 82 : 0, 1500, 240);
  const counts = [openN, resolvedN, pendingN];
  const COLORS = { Open: "#6366f1", Resolved: "#10b981", Pending: "#f59e0b" };
  return (
    <div ref={ref} className="adm-reports-summary-bar">
      {reportsData.map((d, i) => (
        <div
          key={d.category}
          className="adm-reports-summary-item"
          style={{ "--item-color": COLORS[d.category] }}
        >
          <div className="adm-rsummary-top">
            <span
              className="adm-rsummary-dot"
              style={{ background: d.color }}
            />
            <span className="adm-rsummary-label">{d.category}</span>
          </div>
          <p className="adm-rsummary-count">{counts[i]}</p>
          <div className="adm-rsummary-track">
            <div
              className="adm-rsummary-fill"
              style={{
                width: inView ? `${(d.count / total) * 100}%` : "0%",
                background: d.color,
                transition: `width 1.2s cubic-bezier(0.4,0,0.2,1) ${i * 0.12}s`,
              }}
            />
          </div>
        </div>
      ))}
      <div className="adm-rsummary-rate">
        <p className="adm-rsummary-rate-label">Resolution Rate</p>
        <p className="adm-rsummary-rate-value">{rateN}%</p>
      </div>
    </div>
  );
};

const DashboardPage = () => {
  // ✅ localStorage se admin ka naam le lo
  const adminName = localStorage.getItem("firstname") || "Admin";

  const [selectedDateRange, setSelectedDateRange] = useState("Last 30 Days");
  const [dateDropdownOpen, setDateDropdownOpen] = useState(false);
  const [isGeneratingReport, setIsGeneratingReport] = useState(false);
  const [showAnnouncementModal, setShowAnnouncementModal] = useState(false);
  const [announcementData, setAnnouncementData] = useState({
    audience: "all",
    title: "",
    message: "",
    pushNotification: false,
    email: false,
    dashboardBanner: false,
  });
  const [statsRef, statsInView] = useInView();

  const handleDateRangeChange = (r) => {
    setSelectedDateRange(r);
    setDateDropdownOpen(false);
  };
  const handleAnnouncementChange = (f, v) =>
    setAnnouncementData((p) => ({ ...p, [f]: v }));
  const handleSendAnnouncement = () => {
    setAnnouncementData({
      audience: "all",
      title: "",
      message: "",
      pushNotification: false,
      email: false,
      dashboardBanner: false,
    });
    setShowAnnouncementModal(false);
    alert("Announcement sent successfully!");
  };

  const statsCards = [
    {
      id: 1,
      title: "Total Users",
      count: "1,690",
      delta: "+12%",
      deltaUp: true,
      icon: (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="9" cy="7" r="4" />
          <path d="M23 21v-2a4 4 0 0 0-3-3.87" />
          <path d="M16 3.13a4 4 0 0 1 0 7.75" />
        </svg>
      ),
    },
    {
      id: 2,
      title: "Active Roadmaps",
      count: "89",
      delta: "+5%",
      deltaUp: true,
      icon: (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
        </svg>
      ),
    },
    {
      id: 3,
      title: "Active Courses",
      count: "124",
      delta: "+8%",
      deltaUp: true,
      icon: (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
      ),
    },
    {
      id: 4,
      title: "New Enrollers",
      count: "342",
      delta: "-3%",
      deltaUp: false,
      icon: (
        <svg
          width="22"
          height="22"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M16 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2" />
          <circle cx="8.5" cy="7" r="4" />
          <line x1="20" y1="8" x2="20" y2="14" />
          <line x1="23" y1="11" x2="17" y2="11" />
        </svg>
      ),
    },
  ];

  const userGrowthData = [
    { month: "Aug", students: 850, mentors: 320 },
    { month: "Sep", students: 920, mentors: 350 },
    { month: "Oct", students: 1050, mentors: 390 },
    { month: "Nov", students: 1150, mentors: 420 },
    { month: "Dec", students: 1200, mentors: 445 },
    { month: "Jan", students: 1234, mentors: 456 },
  ];
  const roadmapDistribution = [
    { level: "Beginner", count: 32, pct: "36%", color: "#781c2e" },
    { level: "Intermediate", count: 38, pct: "43%", color: "#a52a3a" },
    { level: "Advanced", count: 19, pct: "21%", color: "#c73848" },
  ];
  const aiTutorActivity = [
    { day: "Mon", sessions: 145 },
    { day: "Tue", sessions: 178 },
    { day: "Wed", sessions: 156 },
    { day: "Thu", sessions: 189 },
    { day: "Fri", sessions: 201 },
    { day: "Sat", sessions: 134 },
    { day: "Sun", sessions: 112 },
  ];
  const reportsData = [
    { category: "Open", count: 23, color: "#6366f1" },
    { category: "Resolved", count: 156, color: "#10b981" },
    { category: "Pending", count: 12, color: "#f59e0b" },
  ];
  const recentReports = [
    {
      id: "RPT-001",
      type: "Dispute",
      title: "Payment Issue – Course Refund",
      reportedBy: "Ahmed Khan",
      priority: "High",
      status: "Pending",
      date: "2025-01-10",
    },
    {
      id: "RPT-002",
      type: "Report",
      title: "Inappropriate Behaviour",
      reportedBy: "Sara Ali",
      priority: "Critical",
      status: "Under Review",
      date: "2025-01-10",
    },
    {
      id: "RPT-003",
      type: "Dispute",
      title: "Quiz Grading Dispute",
      reportedBy: "Fatima Malik",
      priority: "Medium",
      status: "Resolved",
      date: "2025-01-09",
    },
    {
      id: "RPT-004",
      type: "Report",
      title: "Plagiarism in Assignment",
      reportedBy: "Bilal Hassan",
      priority: "High",
      status: "Under Review",
      date: "2025-01-09",
    },
    {
      id: "RPT-005",
      type: "Dispute",
      title: "Refund Request – Course",
      reportedBy: "Zainab Yusuf",
      priority: "Medium",
      status: "Pending",
      date: "2025-01-08",
    },
  ];
  const mentorVerifications = [
    {
      id: "DKS",
      name: "Dr. Kamran Siddiqui",
      specialty: "Cloud Computing & DevOps",
      experience: "8 yrs",
      status: "Pending",
      applied: "2025-01-08",
    },
    {
      id: "AR",
      name: "Ayesha Rahman",
      specialty: "UI/UX Design",
      experience: "6 yrs",
      status: "Pending",
      applied: "2025-01-09",
    },
    {
      id: "HM",
      name: "Hassan Malik",
      specialty: "Full Stack Development",
      experience: "10 yrs",
      status: "Approved",
      applied: "2025-01-05",
    },
    {
      id: "FN",
      name: "Fatima Noor",
      specialty: "Data Science & ML",
      experience: "3 yrs",
      status: "Rejected",
      applied: "2025-01-10",
    },
  ];
  const systemActivity = [
    {
      id: 1,
      message: "New user registered",
      time: "2 minutes ago",
      type: "user",
    },
    {
      id: 2,
      message: "Course enrolled",
      time: "15 minutes ago",
      type: "course",
    },
    { id: 3, message: "Mentor approved", time: "1 hour ago", type: "mentor" },
    {
      id: 4,
      message: "System update deployed",
      time: "3 hours ago",
      type: "system",
    },
  ];

  const maxGrowth = Math.max(
    ...userGrowthData.map((d) => Math.max(d.students, d.mentors)),
  );
  const maxActivity = Math.max(...aiTutorActivity.map((d) => d.sessions));
  const totalRoadmaps = roadmapDistribution.reduce((a, b) => a + b.count, 0);

  const buildDonut = (dist, total) => {
    let a = -90;
    return dist.map((d) => {
      const angle = (d.count / total) * 360;
      const e = a + angle;
      const s_ = (a * Math.PI) / 180,
        e_ = (e * Math.PI) / 180;
      const R = 70,
        r = 45,
        cx = 110,
        cy = 100;
      const x1 = cx + R * Math.cos(s_),
        y1 = cy + R * Math.sin(s_);
      const x2 = cx + R * Math.cos(e_),
        y2 = cy + R * Math.sin(e_);
      const x3 = cx + r * Math.cos(e_),
        y3 = cy + r * Math.sin(e_);
      const x4 = cx + r * Math.cos(s_),
        y4 = cy + r * Math.sin(s_);
      const path = `M ${x1} ${y1} A ${R} ${R} 0 ${angle > 180 ? 1 : 0} 1 ${x2} ${y2} L ${x3} ${y3} A ${r} ${r} 0 ${angle > 180 ? 1 : 0} 0 ${x4} ${y4} Z`;
      a = e;
      return { ...d, path };
    });
  };
  const donutSlices = buildDonut(roadmapDistribution, totalRoadmaps);
  const priorityClass = (p) =>
    p === "Critical"
      ? "badge-critical"
      : p === "High"
        ? "badge-high"
        : p === "Medium"
          ? "badge-medium"
          : "badge-low";
  const statusClass = (s) =>
    s === "Resolved" || s === "Approved"
      ? "status-resolved"
      : s === "Under Review"
        ? "status-review"
        : s === "Rejected"
          ? "status-rejected"
          : "status-pending";
  const mentorInitials = (n) =>
    n
      .split(" ")
      .map((x) => x[0])
      .join("")
      .slice(0, 2)
      .toUpperCase();
  const activityIcon = (type) =>
    ({
      user: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
          <circle cx="12" cy="7" r="4" />
        </svg>
      ),
      course: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <path d="M2 3h6a4 4 0 0 1 4 4v14a3 3 0 0 0-3-3H2z" />
          <path d="M22 3h-6a4 4 0 0 0-4 4v14a3 3 0 0 1 3-3h7z" />
        </svg>
      ),
      mentor: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <polyline points="20 6 9 17 4 12" />
        </svg>
      ),
      system: (
        <svg
          width="16"
          height="16"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="3" />
          <path d="M19.07 4.93a10 10 0 0 1 0 14.14M4.93 4.93a10 10 0 0 0 0 14.14" />
        </svg>
      ),
    })[type];

  return (
    <AdminLayout>
      <div className="adm-dashboard-page">
        <div
          className="adm-dashboard-header adm-fade-up"
          style={{ animationDelay: "0s", animationFillMode: "both" }}
        >
          <div className="adm-header-left">
            <h2 className="adm-dashboard-title">Welcome back, {adminName}!</h2>
            <p className="adm-dashboard-subtitle">
              Here's what's happening today.
            </p>
          </div>
          <div className="adm-header-right">
            <div className="adm-date-dropdown-wrapper">
              <button
                className="adm-date-toggle"
                onClick={() => setDateDropdownOpen((v) => !v)}
                aria-expanded={dateDropdownOpen}
              >
                <svg
                  width="14"
                  height="14"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <rect x="3" y="4" width="18" height="18" rx="2" />
                  <line x1="16" y1="2" x2="16" y2="6" />
                  <line x1="8" y1="2" x2="8" y2="6" />
                  <line x1="3" y1="10" x2="21" y2="10" />
                </svg>
                <span>{selectedDateRange}</span>
                <svg
                  width="12"
                  height="12"
                  viewBox="0 0 24 24"
                  fill="none"
                  stroke="currentColor"
                  strokeWidth="2.5"
                >
                  <polyline points="6 9 12 15 18 9" />
                </svg>
              </button>
              {dateDropdownOpen && (
                <div className="adm-date-menu">
                  {[
                    "Today",
                    "Yesterday",
                    "This Week",
                    "Last Week",
                    "Last 30 Days",
                    "All Time",
                  ].map((r) => (
                    <button
                      key={r}
                      className={`adm-date-item${selectedDateRange === r ? " active" : ""}`}
                      onClick={() => handleDateRangeChange(r)}
                    >
                      {r}
                    </button>
                  ))}
                </div>
              )}
            </div>
            <button
              className="adm-primary-action-btn"
              onClick={() => setShowAnnouncementModal(true)}
            >
              <svg
                width="14"
                height="14"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
              >
                <path d="M22 2L11 13" />
                <path d="M22 2L15 22l-4-9-9-4z" />
              </svg>
              Send Announcement
            </button>
          </div>
        </div>

        <div ref={statsRef} className="adm-stats-grid">
          {statsCards.map((card, i) => (
            <StatCard
              key={card.id}
              card={card}
              index={i}
              inView={statsInView}
            />
          ))}
        </div>

        <div
          className="adm-row-2 adm-fade-up"
          style={{ animationDelay: "0.3s", animationFillMode: "both" }}
        >
          <div className="adm-chart-card">
            <div className="adm-chart-header">
              <div>
                <h4 className="adm-chart-title">Users Growth Overview</h4>
                <p className="adm-chart-subtitle">Last 6 months</p>
              </div>
            </div>
            <div className="adm-chart-body">
              <AnimatedLineChart data={userGrowthData} maxVal={maxGrowth} />
              <div className="adm-legend">
                <span className="adm-legend-item">
                  <span
                    className="adm-legend-dot"
                    style={{ background: "#781c2e" }}
                  />
                  Students
                </span>
                <span className="adm-legend-item">
                  <span
                    className="adm-legend-dash"
                    style={{ background: "#a52a3a" }}
                  />
                  Mentors
                </span>
              </div>
            </div>
          </div>
          <div className="adm-chart-card adm-chart-donut">
            <div className="adm-chart-header">
              <div>
                <h4 className="adm-chart-title">Roadmaps Distribution</h4>
                <p className="adm-chart-subtitle">By difficulty level</p>
              </div>
            </div>
            <div className="adm-chart-body adm-donut-body">
              <AnimatedDonut slices={donutSlices} total={totalRoadmaps} />
              <div className="adm-donut-legend">
                {roadmapDistribution.map((d, i) => (
                  <div key={i} className="adm-donut-legend-row">
                    <span
                      className="adm-donut-legend-dot"
                      style={{ background: d.color }}
                    />
                    <span className="adm-donut-legend-label">{d.level}</span>
                    <span className="adm-donut-legend-count">{d.count}</span>
                    <span className="adm-donut-legend-pct">{d.pct}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
          <div className="adm-chart-card adm-chart-bar">
            <div className="adm-chart-header">
              <div>
                <h4 className="adm-chart-title">AI Tutor Activity</h4>
                <p className="adm-chart-subtitle">Weekly usage</p>
              </div>
            </div>
            <div className="adm-chart-body">
              <AnimatedBarChart data={aiTutorActivity} maxVal={maxActivity} />
            </div>
          </div>
        </div>

        <ReportsSummaryBar reportsData={reportsData} />

        <div
          className="adm-section-card adm-fade-up"
          style={{ animationDelay: "0.45s", animationFillMode: "both" }}
        >
          <div className="adm-section-header">
            <div>
              <h4 className="adm-section-title">Recent Reports</h4>
              <p className="adm-section-subtitle">
                Latest 5 platform reports and disputes
              </p>
            </div>
            <a href="#" className="adm-view-all-link">
              View All Reports →
            </a>
          </div>
          <div className="adm-table-wrapper">
            <table className="adm-table">
              <thead>
                <tr>
                  <th>ID</th>
                  <th>Type</th>
                  <th>Title</th>
                  <th>Reported By</th>
                  <th>Priority</th>
                  <th>Status</th>
                  <th>Date</th>
                  <th>Action</th>
                </tr>
              </thead>
              <tbody>
                {recentReports.map((r) => (
                  <tr key={r.id}>
                    <td className="adm-table-id">{r.id}</td>
                    <td>
                      <span
                        className={`adm-type-badge ${r.type === "Dispute" ? "type-dispute" : "type-report"}`}
                      >
                        {r.type}
                      </span>
                    </td>
                    <td className="adm-table-title">{r.title}</td>
                    <td className="adm-table-secondary">{r.reportedBy}</td>
                    <td>
                      <span
                        className={`adm-priority-badge ${priorityClass(r.priority)}`}
                      >
                        {r.priority}
                      </span>
                    </td>
                    <td>
                      <span
                        className={`adm-status-badge ${statusClass(r.status)}`}
                      >
                        {r.status}
                      </span>
                    </td>
                    <td className="adm-table-secondary">{r.date}</td>
                    <td>
                      <button className="adm-view-btn">View</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div
          className="adm-section-card adm-fade-up"
          style={{ animationDelay: "0.55s", animationFillMode: "both" }}
        >
          <div className="adm-section-header">
            <div>
              <h4 className="adm-section-title">Mentor Verification</h4>
              <p className="adm-section-subtitle">
                Pending and recent mentor applications
              </p>
            </div>
            <div className="adm-mentor-header-right">
              <span className="adm-mentor-pending-badge">2 Pending</span>
              <a href="#" className="adm-view-all-link">
                Manage All →
              </a>
            </div>
          </div>
          <div className="adm-mentor-grid">
            {mentorVerifications.map((m, i) => (
              <div
                key={m.id}
                className={`adm-mentor-card adm-fade-up ${m.status === "Pending" ? "mentor-card-pending" : ""}`}
                style={{
                  animationDelay: `${0.55 + i * 0.08}s`,
                  animationFillMode: "both",
                }}
              >
                <div className="adm-mentor-card-top">
                  <div className="adm-mentor-avatar">
                    {mentorInitials(m.name)}
                  </div>
                  <div className="adm-mentor-info">
                    <p className="adm-mentor-name">{m.name}</p>
                    <p className="adm-mentor-specialty">{m.specialty}</p>
                  </div>
                  <span className={`adm-status-badge ${statusClass(m.status)}`}>
                    {m.status}
                  </span>
                </div>
                <div className="adm-mentor-card-meta">
                  <span className="adm-mentor-meta-item">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="2" y="7" width="20" height="14" rx="2" />
                      <path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16" />
                    </svg>
                    {m.experience}
                  </span>
                  <span className="adm-mentor-meta-item">
                    <svg
                      width="12"
                      height="12"
                      viewBox="0 0 24 24"
                      fill="none"
                      stroke="currentColor"
                      strokeWidth="2"
                    >
                      <rect x="3" y="4" width="18" height="18" rx="2" />
                      <line x1="16" y1="2" x2="16" y2="6" />
                      <line x1="8" y1="2" x2="8" y2="6" />
                      <line x1="3" y1="10" x2="21" y2="10" />
                    </svg>
                    Applied {m.applied}
                  </span>
                </div>
                {m.status === "Pending" && (
                  <div className="adm-mentor-actions">
                    <button className="adm-approve-btn">Approve</button>
                    <button className="adm-reject-btn">Reject</button>
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>

        <div
          className="adm-section-card adm-fade-up"
          style={{ animationDelay: "0.65s", animationFillMode: "both" }}
        >
          <div className="adm-section-header">
            <div>
              <h4 className="adm-section-title">System Activity</h4>
              <p className="adm-section-subtitle">Recent platform events</p>
            </div>
          </div>
          <div className="adm-activity-list">
            {systemActivity.map((a, i) => (
              <div key={a.id} className="adm-activity-row">
                <div className="adm-activity-timeline">
                  <div className="adm-activity-icon-wrap">
                    {activityIcon(a.type)}
                  </div>
                  {i < systemActivity.length - 1 && (
                    <div className="adm-activity-line" />
                  )}
                </div>
                <div className="adm-activity-body">
                  <p className="adm-activity-msg">{a.message}</p>
                  <span className="adm-activity-time">{a.time}</span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {showAnnouncementModal && (
          <div
            className="adm-modal-overlay"
            onClick={() => setShowAnnouncementModal(false)}
          >
            <div
              className="adm-modal"
              onClick={(e) => e.stopPropagation()}
              role="dialog"
              aria-modal="true"
            >
              <div className="adm-modal-header">
                <h3 className="adm-modal-title">Send New Announcement</h3>
                <button
                  className="adm-modal-close"
                  onClick={() => setShowAnnouncementModal(false)}
                  aria-label="Close"
                >
                  <svg
                    width="18"
                    height="18"
                    viewBox="0 0 24 24"
                    fill="none"
                    stroke="currentColor"
                    strokeWidth="2"
                  >
                    <line x1="18" y1="6" x2="6" y2="18" />
                    <line x1="6" y1="6" x2="18" y2="18" />
                  </svg>
                </button>
              </div>
              <div className="adm-modal-body">
                <div className="adm-form-group">
                  <label className="adm-form-label">Target Audience</label>
                  <select
                    className="adm-form-control"
                    value={announcementData.audience}
                    onChange={(e) =>
                      handleAnnouncementChange("audience", e.target.value)
                    }
                  >
                    <option value="all">All Users</option>
                    <option value="students">Students Only</option>
                    <option value="mentors">Mentors Only</option>
                  </select>
                </div>
                <div className="adm-form-group">
                  <label className="adm-form-label">Announcement Title</label>
                  <input
                    type="text"
                    className="adm-form-control"
                    placeholder="Enter announcement title"
                    value={announcementData.title}
                    onChange={(e) =>
                      handleAnnouncementChange("title", e.target.value)
                    }
                  />
                </div>
                <div className="adm-form-group">
                  <label className="adm-form-label">Message</label>
                  <div className="adm-editor-toolbar">
                    {["B", "I", "U"].map((t) => (
                      <button
                        key={t}
                        type="button"
                        className="adm-toolbar-btn"
                        title={t}
                      >
                        <span
                          style={{
                            fontStyle: t === "I" ? "italic" : "normal",
                            fontWeight: t === "B" ? 700 : 400,
                            textDecoration: t === "U" ? "underline" : "none",
                          }}
                        >
                          {t}
                        </span>
                      </button>
                    ))}
                    <button
                      type="button"
                      className="adm-toolbar-btn"
                      title="Link"
                    >
                      <svg
                        width="14"
                        height="14"
                        viewBox="0 0 24 24"
                        fill="none"
                        stroke="currentColor"
                        strokeWidth="2"
                      >
                        <path d="M10 13a5 5 0 0 0 7.54.54l3-3a5 5 0 0 0-7.07-7.07l-1.72 1.71" />
                        <path d="M14 11a5 5 0 0 0-7.54-.54l-3 3a5 5 0 0 0 7.07 7.07l1.71-1.71" />
                      </svg>
                    </button>
                  </div>
                  <textarea
                    className="adm-form-control adm-textarea"
                    rows={5}
                    placeholder="Write your announcement message here…"
                    value={announcementData.message}
                    onChange={(e) =>
                      handleAnnouncementChange("message", e.target.value)
                    }
                  />
                </div>
                <div className="adm-form-group">
                  <label className="adm-form-label">
                    Notification Channels
                  </label>
                  {[
                    { key: "pushNotification", label: "Push Notification" },
                    { key: "email", label: "Email" },
                    { key: "dashboardBanner", label: "Dashboard Banner" },
                  ].map(({ key, label }) => (
                    <label key={key} className="adm-checkbox-row">
                      <input
                        type="checkbox"
                        className="adm-checkbox"
                        checked={announcementData[key]}
                        onChange={(e) =>
                          handleAnnouncementChange(key, e.target.checked)
                        }
                      />
                      <span>{label}</span>
                    </label>
                  ))}
                </div>
              </div>
              <div className="adm-modal-footer">
                <button
                  className="adm-modal-cancel-btn"
                  onClick={() => setShowAnnouncementModal(false)}
                >
                  Cancel
                </button>
                <button
                  className="adm-modal-primary-btn"
                  onClick={handleSendAnnouncement}
                >
                  Send Announcement
                </button>
              </div>
            </div>
          </div>
        )}
      </div>
    </AdminLayout>
  );
};

export default DashboardPage;
